import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import './camp-de-jour-step.css';
import { getSemainesCampJour } from '../../../server/services/catalogue-service';
import { addBilleterieTemp, deleteReservationTemp } from '../../../server/services/reservation-service';
import { addToShoppingCart, CartItemType, removeFromShoppingCart, updateShop } from '../../actions/shop';
import LoadingSpinner from '../../svg/loading';
import { CACHE_KEY_NOMBRE_ENFANTS, MAX_ENFANTS, setCachedNombreEnfants, clearCachedNombreEnfants } from './camp-de-jour-constants';
import { useHasCampJourInCart } from './hooks/useHasCampJourInCart';
import { clearFieldError } from './camp-de-jour-step-utils';
import { validateCampDeJourStep, restoreSelectionsFromCart } from './camp-de-jour-step-utils';

function getCampJourItemIdPrefix(childNum) {
  return `inscriptionCampJour_enfant${childNum}`;
}

function getCampJourCartItemForChild(cart, childNum) {
  if (!Array.isArray(cart)) return undefined;
  const baseId = getCampJourItemIdPrefix(childNum);
  const legacyPrefix = `${baseId}_`;
  return cart.find(item =>
    item?.type === CartItemType.CampJour && (item?.id === baseId || item?.id?.startsWith(legacyPrefix))
  );
}

function getCampJourCartItemsForChild(cart, childNum) {
  if (!Array.isArray(cart)) return [];
  const baseId = getCampJourItemIdPrefix(childNum);
  const legacyPrefix = `${baseId}_`;
  return cart.filter(item =>
    item?.type === CartItemType.CampJour && (item?.id === baseId || item?.id?.startsWith(legacyPrefix))
  );
}

function getLegacyCampJourItemForWeek(cart, childNum, weekId) {
  if (!Array.isArray(cart)) return undefined;
  const id = `${getCampJourItemIdPrefix(childNum)}_${weekId}`;
  return cart.find(item => item?.type === CartItemType.CampJour && item?.id === id);
}

function formatWeekLabel(label, language) {
  if (language !== 'en' || typeof label !== 'string') return label;
  return label.replace(/\bSemaine\b/g, 'Week').replace(/\bsemaine\b/g, 'week');
}

/**
 * Custom step component for Camp de Jour form
 * Props:
 * - currentStep: current step index
 * - totalSteps: total number of steps
 * - goToNextStep: function to go to next step
 * - goToPreviousStep: function to go to previous step
 * - goToStep: function to go to a specific step
 * - formData: current form data object
 * - onChange: function to update a field value (fieldId, value)
 * - setFormData: function to update the entire form data
 * - validate: function to validate the current step
 * - match: React Router match object
 * - language: current language
 */
const CampDeJourStep = ({
  currentStep,
  totalSteps,
  goToNextStep,
  goToPreviousStep,
  goToStep,
  formData,
  onChange,
  setFormData,
  validate,
  language,
  codeClient = ''
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const shop = useSelector(state => state.shop);
  const hasCampJourInCart = useHasCampJourInCart();
  const DELAY_SPIN_INPUT = 750;

  const t = {
    titleStep1CampDeJour: language === 'fr' ? 'Sélection de la période' : 'Period Selection',
    jAmeneMonEnfant: language === 'fr' ? "J'amène mon enfant" : "I bring my child",
    nombreEnfants: language === 'fr' ? 'Nombre d\'enfants' : 'Number of children',
    nombreEnfantsPlaceholder: language === 'fr' ? 'Entrez le nombre d\'enfants' : 'Enter number of children',
    nombreEnfantsRequired: language === 'fr' ? 'Le nombre d\'enfants est requis' : 'Number of children is required',
    nombreEnfantsInvalid: language === 'fr' ? 'Le nombre d\'enfants doit être au moins 1' : 'Number of children must be at least 1',
    nombreEnfantsInvalidMax: language === 'fr' ? 'Le nombre d\'enfants ne peut pas dépasser 99' : 'Number of children cannot exceed 99',
    precedent: language === 'fr' ? 'Précédent' : 'Previous',
    suivant: language === 'fr' ? 'Suivant' : 'Next',
    panierCampJourRequired: language === 'fr'
      ? 'Veuillez sélectionner au moins une semaine avec un trajet (le panier doit contenir une inscription camp de jour).'
      : 'Please select at least one week with a route (the cart must contain a day camp registration).',
  };

  const getInitialNombreEnfants = () => {
    const cached = sessionStorage.getItem(CACHE_KEY_NOMBRE_ENFANTS);
    const value = cached || formData.nombreEnfants || '';
    const num = parseInt(value, 10);
    if (value === '' || isNaN(num) || num < 1) {
      return '1';
    }
    if (num > MAX_ENFANTS) {
      return String(MAX_ENFANTS);
    }
    return value;
  };

  const getRestoredSelections = (numEnfants) =>
    restoreSelectionsFromCart({ numEnfants, cart: shop?.cart ?? [], formData });

  const [nombreEnfants, setNombreEnfants] = useState(getInitialNombreEnfants);
  const [errors, setErrors] = useState({});
  const [semainesData, setSemainesData] = useState([]);
  const [isLoadingSemaines, setIsLoadingSemaines] = useState(true);
  const [isTrajetLoading, setIsTrajetLoading] = useState(false);

  const [enfantsSelections, setEnfantsSelections] = useState(() => {
    const numEnfants = parseInt(getInitialNombreEnfants(), 10) || 0;
    return restoreSelectionsFromCart({ numEnfants, cart: shop?.cart ?? [], formData });
  });

  const idsReservationTempMapRef = useRef({});
  const fetchSemainesTimeoutRef = useRef(null);

  const fetchSemaines = useCallback(async (shopOverride) => {
    const currentShop = shopOverride !== undefined ? shopOverride : shop;
    try {
      setIsLoadingSemaines(true);
      const response = await getSemainesCampJour(currentShop);
      const data = response?.data || response || [];
      const safeData = Array.isArray(data) ? data : [];
      setSemainesData(safeData);
    } catch (err) {
      setSemainesData([]);
    } finally {
      setIsLoadingSemaines(false);
    }
  }, [shop]);

  useEffect(() => {
    if (!formData.nombreEnfants) {
      const cached = sessionStorage.getItem(CACHE_KEY_NOMBRE_ENFANTS);
      const num = parseInt(cached, 10);
      let value = '1';
      if (cached && !isNaN(num) && num >= 1 && num <= MAX_ENFANTS) {
        value = cached;
      } else if (cached && !isNaN(num) && num > MAX_ENFANTS) {
        value = String(MAX_ENFANTS);
      }
      onChange('nombreEnfants', value);
    }
  }, [formData.nombreEnfants, onChange]);

  useEffect(() => {
    fetchSemaines();
  }, []);

  useEffect(() => {
    const numEnfants = parseInt(nombreEnfants) || 0;
    if (numEnfants > 0 && semainesData.length > 0 && shop.cart.length > 0) {
      const restoredSelections = getRestoredSelections(numEnfants);

      const hasSelections = Object.keys(restoredSelections).some(
        childIndex => Object.keys(restoredSelections[childIndex] || {}).length > 0
      );

      if (hasSelections) {
        setEnfantsSelections(restoredSelections);

        Object.keys(restoredSelections).forEach(childIndex => {
          const fieldId = `enfant${parseInt(childIndex) + 1}_semaines`;
          onChange(fieldId, restoredSelections[childIndex]);
        });
      }
    }
  }, [shop.cart, semainesData.length, nombreEnfants]);

  // If week become FULL with no more trajet
  // Deselect all children who had it
  useEffect(() => {
    if (!Array.isArray(semainesData) || semainesData.length === 0 || !onChange) return;

    setEnfantsSelections(prev => {
      const selections = prev || {};
      const weekSelectionCountMap = {};
      Object.entries(selections).forEach(([, childWeeks]) => {
        Object.entries(childWeeks || {}).forEach(([weekId, weekData]) => {
          if (weekData?.selected && weekData?.trajet) {
            weekSelectionCountMap[weekId] = (weekSelectionCountMap[weekId] || 0) + 1;
          }
        });
      });

      const weekPlacesMap = {};
      semainesData.forEach(item => {
        const wid = item.idProduitAge;
        if (wid && weekPlacesMap[wid] === undefined) {
          weekPlacesMap[wid] = item.placesRestantesSemaine ?? 0;
        }
      });

      const fullWeekIds = new Set(
        Object.keys(weekPlacesMap).filter(weekId => {
          const places = weekPlacesMap[weekId] || 0;
          const count = weekSelectionCountMap[weekId] || 0;
          return (places - count) <= 0;
        })
      );

      let changed = false;
      const newSelections = {};
      Object.entries(selections).forEach(([childIndex, childWeeks]) => {
        newSelections[childIndex] = {};
        Object.entries(childWeeks || {}).forEach(([weekId, weekData]) => {
          const isFull = fullWeekIds.has(weekId);
          const hasNoTrajet = !weekData?.trajet;
          const isSelected = weekData?.selected;
          if (isSelected && hasNoTrajet && isFull) {
            newSelections[childIndex][weekId] = { selected: false, trajet: null };
            changed = true;
          } else {
            newSelections[childIndex][weekId] = { ...weekData };
          }
        });
      });

      if (!changed) return prev;

      Object.keys(newSelections).forEach(ci => {
        const fieldId = `enfant${parseInt(ci, 10) + 1}_semaines`;
        onChange(fieldId, newSelections[ci]);
      });

      return newSelections;
    });
  }, [semainesData, onChange]);

  const handleNombreEnfantsChange = (e) => {
    let val = e.target.value;

    // Only allow numbers
    val = val.replace(/[^0-9]/g, '');
    // Remove leading zeros
    val = val.replace(/^0+/, '');

    const prevNumEnfants = parseInt(nombreEnfants) || 0;
    let numEnfants = val === '' ? 0 : parseInt(val, 10);

    if (val !== '' && (isNaN(numEnfants) || numEnfants < 0)) {
      return;
    }

    if (numEnfants === 0) {
      setNombreEnfants('');
      onChange('nombreEnfants', '');
      clearCachedNombreEnfants();

      const cart = store.getState().shop.cart || [];
      const allIdsToDelete = [];

      Object.values(idsReservationTempMapRef.current || {}).forEach((weekIdsMap) => {
        if (weekIdsMap && typeof weekIdsMap === 'object') {
          allIdsToDelete.push(...Object.values(weekIdsMap).flat());
        }
      });

      for (let childIndex = 0; childIndex < prevNumEnfants; childIndex++) {
        const childNum = childIndex + 1;
        const fieldId = `enfant${childNum}_semaines`;
        onChange(fieldId, {});
      }

      cart.forEach((cartItem) => {
        if (Array.isArray(cartItem.idsReservationTemp) && cartItem.idsReservationTemp.length > 0) {
          allIdsToDelete.push(...cartItem.idsReservationTemp);
        }
        dispatch(removeFromShoppingCart(cartItem, false, language));
      });

      const uniqueIdsToDelete = [...new Set(allIdsToDelete)];
      if (uniqueIdsToDelete.length > 0) {
        deleteReservationTemp(uniqueIdsToDelete).catch((err) =>
          console.error('Error deleting reservation temp when nombreEnfants is 0', err)
        );
      }

      idsReservationTempMapRef.current = {};
      setEnfantsSelections({});

      if (errors.nombreEnfants) {
        setErrors(prev => ({ ...prev, nombreEnfants: '' }));
      }

      if (fetchSemainesTimeoutRef.current) {
        clearTimeout(fetchSemainesTimeoutRef.current);
      }
      fetchSemainesTimeoutRef.current = setTimeout(() => {
        fetchSemaines(store.getState().shop);
      }, DELAY_SPIN_INPUT);

      return;
    }

    if (numEnfants < 1) {
      val = '1';
      numEnfants = 1;
    } else if (numEnfants > MAX_ENFANTS) {
      val = String(MAX_ENFANTS);
      numEnfants = MAX_ENFANTS;
    }

    setNombreEnfants(val);
    onChange('nombreEnfants', val);

    setCachedNombreEnfants(val);

    if (numEnfants > 0) {
      setEnfantsSelections(prev => {
        const newSelections = { ...prev };
        for (let i = 0; i < numEnfants; i++) {
          if (!newSelections[i]) {
            newSelections[i] = {};
          }
        }
        Object.keys(newSelections).forEach(key => {
          if (parseInt(key) >= numEnfants) {
            delete newSelections[key];
          }
        });
        return newSelections;
      });
    }

    if (numEnfants < prevNumEnfants) {
      const cart = store.getState().shop.cart || [];
      const allIdsToDelete = [];

      for (let childIndex = numEnfants; childIndex < prevNumEnfants; childIndex++) {
        const childNum = childIndex + 1;
        const weekIdsMap = idsReservationTempMapRef.current[childIndex];
        if (weekIdsMap && typeof weekIdsMap === 'object') {
          const ids = Object.values(weekIdsMap).flat();
          allIdsToDelete.push(...ids);
        }
        delete idsReservationTempMapRef.current[childIndex];

        const itemsToRemove = getCampJourCartItemsForChild(cart, childNum);
        itemsToRemove.forEach((cartItem) => {
          if (Array.isArray(cartItem.idsReservationTemp) && cartItem.idsReservationTemp.length > 0) {
            allIdsToDelete.push(...cartItem.idsReservationTemp);
          }
          dispatch(removeFromShoppingCart(cartItem, false, language));
        });

        const fieldId = `enfant${childNum}_semaines`;
        onChange(fieldId, {});
      }

      const uniqueIdsToDelete = [...new Set(allIdsToDelete)];
      if (uniqueIdsToDelete.length > 0) {
        deleteReservationTemp(uniqueIdsToDelete).catch((err) => console.error('Error deleting reservation temp for removed children', err));
      }
    }

    if (errors.nombreEnfants) {
      setErrors(prev => ({ ...prev, nombreEnfants: '' }));
    }

    if (fetchSemainesTimeoutRef.current) {
      clearTimeout(fetchSemainesTimeoutRef.current);
    }
    fetchSemainesTimeoutRef.current = setTimeout(() => {
      fetchSemaines(store.getState().shop);
    }, DELAY_SPIN_INPUT);
  };

  const getUniqueWeeks = () => {
    if (!Array.isArray(semainesData) || semainesData.length === 0) {
      return [];
    }

    const trajetSelectionCountMap = {};
    const weekSelectionCountMap = {};
    Object.values(enfantsSelections || {}).forEach((childWeeks) => {
      Object.entries(childWeeks || {}).forEach(([weekId, weekData]) => {
        if (weekData && weekData.selected && weekData.trajet) {
          const key = `${weekId}|${weekData.trajet}`;
          trajetSelectionCountMap[key] = (trajetSelectionCountMap[key] || 0) + 1;
          weekSelectionCountMap[weekId] = (weekSelectionCountMap[weekId] || 0) + 1;
        }
      });
    });

    const weeksMap = new Map();
    const semaineBlockedMap = new Map();

    semainesData.forEach(item => {
      const semaine = item.semaine;
      const placesRestantesSemaine = item.placesRestantesSemaine || 0;
      const placesRestantesAge = item.placesRestantesAge || 0;

      if (semaine) {
        if (placesRestantesSemaine <= 0 || placesRestantesAge <= 0) {
          semaineBlockedMap.set(semaine, true);
        }
      }
    });

    semainesData.forEach(item => {
      const semaine = item.semaine;
      const idProduitAge = item.idProduitAge;
      const dateDebut = item.dateDebut;
      const dateFin = item.dateFin;
      const trajet = item.trajet;
      const autobus = item.autobus;
      const placesRestantesSemaine = item.placesRestantesSemaine;
      const placesRestantesAge = item.placesRestantesAge;
      const placesRestantesTrajet = item.placesRestantesTrajet;
      const idProduitTrajet = item.idProduitTrajet;
      const idProduitPrixTrajet = item.idProduitPrixTrajet;

      if (semaine && idProduitAge) {
        const isBlocked = semaineBlockedMap.get(semaine) || false;

        if (!weeksMap.has(semaine)) {
          const countSelectedForWeek = weekSelectionCountMap[idProduitAge] || 0;
          const placesRestantesEffectiveSemaine = Math.max(0, (placesRestantesSemaine || 0) - countSelectedForWeek);
          weeksMap.set(semaine, {
            id: idProduitAge,
            label: semaine,
            dateDebut,
            dateFin,
            trajets: [],
            placesRestantesSemaine,
            placesRestantesAge,
            placesRestantesEffectiveSemaine,
            isBlocked
          });
        }

        const weekData = weeksMap.get(semaine);

        if (!isBlocked && trajet) {
          const uniqueId = idProduitPrixTrajet || idProduitTrajet || trajet;
          const trajetExists = weekData.trajets.some(t => t.value === uniqueId);
          if (!trajetExists) {
            const countSelected = trajetSelectionCountMap[`${idProduitAge}|${uniqueId}`] || 0;
            const countSelectedForWeek = weekSelectionCountMap[idProduitAge] || 0;
            const placesRestantesEffective = Math.max(0, (placesRestantesTrajet || 0) - countSelected);
            const placesRestantesEffectiveSemaine = Math.max(0, (placesRestantesSemaine || 0) - countSelectedForWeek);
            const isTrajetDisabledByTrajet = placesRestantesEffective <= 0;
            const isTrajetDisabledBySemaine = placesRestantesEffectiveSemaine <= 0;
            const isTrajetDisabled = isTrajetDisabledByTrajet || isTrajetDisabledBySemaine;
            const minPlaces = isTrajetDisabled ? 0 : Math.min(
              placesRestantesAge,
              placesRestantesSemaine,
              placesRestantesEffective,
              placesRestantesEffectiveSemaine
            );

            const trajetLabel = trajet === 'Aucun' ? t.jAmeneMonEnfant : trajet;
            weekData.trajets.push({
              value: uniqueId,
              label: trajetLabel,
              originalTrajet: trajet,
              autobus: autobus,
              placesRestantes: placesRestantesEffective,
              placesRestantesAge: placesRestantesAge,
              placesRestantesSemaine: placesRestantesSemaine,
              minPlaces,
              isDisabled: isTrajetDisabled
            });
          }
        }
      }
    });

    return Array.from(weeksMap.values()).sort((a, b) => {
      if (a.dateDebut && b.dateDebut) {
        return new Date(a.dateDebut) - new Date(b.dateDebut);
      }
      return a.label.localeCompare(b.label);
    });
  };

  const addWeekToReservationTemp = async (childIndex, weekId, trajetValue = null) => {
    if (!semainesData || semainesData.length === 0) {
      return;
    }

    let busData;
    if (trajetValue && trajetValue !== 'Aucun') {
      busData = semainesData.find(item => {
        const itemWeekId = item.idProduitAge;
        const itemTrajetId = item.idProduitTrajet;
        const itemPrixTrajetId = item.idProduitPrixTrajet;

        return itemWeekId === weekId &&
          (itemTrajetId === trajetValue || itemPrixTrajetId === trajetValue);
      });
    } else {
      busData = semainesData.find(item => item.idProduitAge === weekId);
    }

    busData.codeClient = (codeClient || '').trim();

    if (!busData) {
      return;
    }

    // Only call addBilleterieTemp when a trajet is selected 
    if (trajetValue === undefined || trajetValue === null) {
      return;
    }

    const idProduit = 'CCCCCCCC-CCCC-CCCC-CCCC-CCCCCCCCCCCC'

    try {
      const requestData = {
        idProduit: idProduit,
        quantite: 1,
        jsonData: busData
      };

      const result = await addBilleterieTemp(requestData);
      if (result) {
        if (!idsReservationTempMapRef.current[childIndex]) {
          idsReservationTempMapRef.current[childIndex] = {};
        }
        idsReservationTempMapRef.current[childIndex][weekId] = [result];
        return { idsReservationTemp: [result] };
      } else {
        // console.error('No reservation temp ID returned', result);
      }
    } catch (error) {
      // console.error(`Error adding week ${weekId} to reservation temp:`, error);
    }
  };

  const removeCampJourFromCartAndRef = (childIndex, weekId) => {
    const childNum = childIndex + 1;
    const idsToRemove = idsReservationTempMapRef.current[childIndex]?.[weekId] ?? [];
    if (idsReservationTempMapRef.current[childIndex]) {
      delete idsReservationTempMapRef.current[childIndex][weekId];
    }

    const cart = store.getState().shop.cart || [];

    const legacyItem = getLegacyCampJourItemForWeek(cart, childNum, weekId);
    if (legacyItem) {
      dispatch(removeFromShoppingCart(legacyItem, false, language));
      return;
    }

    const cartItem = getCampJourCartItemForChild(cart, childNum);
    if (!cartItem) return;

    const newTrajets = (cartItem.trajetsAutobus || []).filter(t => t.idProduitAge !== weekId);
    const newIds = (cartItem.idsReservationTemp || []).filter(id => !idsToRemove.includes(id));
    if (newTrajets.length === 0) {
      dispatch(removeFromShoppingCart(cartItem, false, language));
      return;
    }

    const weeksMontant = newTrajets.reduce((s, t) => s + (Number(t.prix) || 0), 0);
    const productMontant = Number(cartItem.total) - (cartItem.trajetsAutobus || []).reduce((s, t) => s + (Number(t.prix) || 0), 0);
    const montant = weeksMontant + Math.max(0, productMontant);
    const trajetDisplay = (tr) => (tr === 'Aucun' ? (language === 'fr' ? "J'amène mon enfant" : "I bring my child") : tr);
    const inclus = [
      { name: language === 'fr' ? 'Enfant' : 'Child', value: childNum },
      ...newTrajets.map(t => ({ name: t.semaine, value: trajetDisplay(t.trajet) }))
    ];
    const weekNames = newTrajets.map(t => t.semaine).filter(Boolean).join(', ');
    const name = weekNames
      ? (language === 'fr' ? `Camp de jour - Enfant ${childNum} - ${weekNames}` : `Day camp - Child ${childNum} - ${weekNames}`)
      : (language === 'fr' ? `Camp de jour - Enfant ${childNum}` : `Day camp - Child ${childNum}`);

    const updatedItem = {
      ...cartItem,
      trajetsAutobus: newTrajets,
      idsReservationTemp: newIds,
      inscriptionCamp: { ...(cartItem.inscriptionCamp || {}), trajetsAutobus: newTrajets },
      inclus,
      name,
      montant,
      taxableTps: false,
      taxableTvq: false,
      total: montant,
      tps: 0,
      tvq: 0
    };
    const idx = cart.findIndex(item => item === cartItem);
    if (idx >= 0) dispatch(updateShop(idx, updatedItem, false, language));
  };

  const handleWeekToggle = (childIndex, weekId) => {
    const current = enfantsSelections[childIndex]?.[weekId];
    const isNowSelected = !current?.selected;
    const fieldId = `enfant${childIndex + 1}_semaines`;

    if (isNowSelected) {
      setErrors(prev => clearFieldError(prev, fieldId));
    }

    setEnfantsSelections(prev => {
      const newSelections = { ...prev };
      if (!newSelections[childIndex]) {
        newSelections[childIndex] = {};
      }
      const currentInUpdater = newSelections[childIndex][weekId];
      const isSelecting = !currentInUpdater?.selected;
      newSelections[childIndex][weekId] = {
        selected: isSelecting,
        trajet: currentInUpdater?.trajet || null
      };

      if (!newSelections[childIndex][weekId].selected) {
        newSelections[childIndex][weekId].trajet = null;
      }

      onChange(fieldId, newSelections[childIndex]);

      if (isSelecting) {
        const trajetForWeek = newSelections[childIndex][weekId].trajet;
        addWeekToReservationTemp(childIndex, weekId, trajetForWeek).then(() =>
          updateCampJourCartItem(childIndex, newSelections[childIndex])
        ).then(() => {
          fetchSemaines(store.getState().shop);
        });
      } else {
        const idsToDelete = idsReservationTempMapRef.current[childIndex]?.[weekId];
        if (idsToDelete && idsToDelete.length > 0) {
          deleteReservationTemp(idsToDelete)
            .then(() => removeCampJourFromCartAndRef(childIndex, weekId))
            .then(() => fetchSemaines(store.getState().shop))
            .catch((err) => {
              // console.error('Error deleting reservation temp for week', weekId, err);
              removeCampJourFromCartAndRef(childIndex, weekId);
              fetchSemaines(store.getState().shop);
            });
        } else {
          removeCampJourFromCartAndRef(childIndex, weekId);
          fetchSemaines(store.getState().shop);
        }
      }

      return newSelections;
    });
  };

  const updateCampJourCartItem = async (childIndex, childSelectionsOverride = null) => {
    const childNum = childIndex + 1;
    const childSelections = childSelectionsOverride || enfantsSelections[childIndex] || {};
    const selectedWeeks = Object.entries(childSelections).filter(([_, week]) => week.selected && week.trajet);

    const cart = store.getState().shop.cart || [];
    const campItemId = getCampJourItemIdPrefix(childNum);

    const existingItem = getCampJourCartItemForChild(cart, childNum);
    const existingSelectedProducts = existingItem?.inscriptionCamp?.selectedProducts ?? [];
    const existingProductTotal = existingItem ? (Number(existingItem.total) - (existingItem.trajetsAutobus || []).reduce((s, t) => s + (Number(t.prix) || 0), 0)) : 0;

    if (selectedWeeks.length === 0) {
      if (existingItem) {
        await dispatch(removeFromShoppingCart(existingItem, false, language));
      }
      const legacyPrefix = `${campItemId}_`;
      const legacyItems = cart.filter(
        item => item?.type === CartItemType.CampJour && item?.id?.startsWith(legacyPrefix)
      );
      for (const item of legacyItems) {
        await dispatch(removeFromShoppingCart(item, false, language));
      }
      return;
    }

    const allTrajetsAutobus = [];
    const allIdsReservationTemp = [];
    const inclus = [{ name: language === 'fr' ? 'Enfant' : 'Child', value: childNum }];
    let weeksMontant = 0;

    for (const [weekId, weekData] of selectedWeeks) {
      let busData;
      if (weekData.trajet && weekData.trajet !== 'Aucun' && weekData.trajet !== t.jAmeneMonEnfant) {
        busData = semainesData.find(item => {
          const itemWeekId = item.idProduitAge;
          const itemTrajetId = item.idProduitTrajet;
          const itemPrixTrajetId = item.idProduitPrixTrajet;
          return itemWeekId === weekId &&
            (itemTrajetId === weekData.trajet || itemPrixTrajetId === weekData.trajet);
        });
      } else {
        busData = semainesData.find(item => item.idProduitAge === weekId);
      }

      if (!busData) continue;

      const prix = (busData.prix != null && busData.prix !== '') ? Number(busData.prix) : 0;
      const semaine = busData.semaine || weekId;
      const trajet = busData.trajet || weekData.trajet;

      weeksMontant += prix;
      allTrajetsAutobus.push({
        semaine,
        trajet,
        idProduitTrajet: busData.idProduitTrajet || weekData.trajet,
        idProduitPrixTrajet: busData.idProduitPrixTrajet,
        prix,
        idProduitAge: weekId,
        idProduitPrixAge: busData.idProduitPrixAge,
        ageDebut: busData.ageDebut,
        ageFin: busData.ageFin
      });
      const trajetDisplay = trajet === 'Aucun' ? t.jAmeneMonEnfant : trajet;
      inclus.push({ name: semaine, value: trajetDisplay });

      const idsForWeek = idsReservationTempMapRef.current[childIndex]?.[weekId] ?? [];
      if (Array.isArray(idsForWeek)) allIdsReservationTemp.push(...idsForWeek);
    }

    if (existingItem?.idsReservationTemp?.length) {
      const existingIds = new Set(allIdsReservationTemp);
      existingItem.idsReservationTemp.forEach(id => { if (id && !existingIds.has(id)) { existingIds.add(id); allIdsReservationTemp.push(id); } });
    }

    const inscriptionCamp = {
      trajetsAutobus: [...allTrajetsAutobus],
      selectedProducts: [...existingSelectedProducts]
    };
    const montant = weeksMontant + existingProductTotal;
    const weekNames = allTrajetsAutobus.map(t => t.semaine).filter(Boolean).join(', ');
    const name = weekNames
      ? (language === 'fr' ? `Camp de jour - Enfant ${childNum} - ${weekNames}` : `Day camp - Child ${childNum} - ${weekNames}`)
      : (language === 'fr' ? `Camp de jour - Enfant ${childNum}` : `Day camp - Child ${childNum}`);

    const firstIdProduitAge = allTrajetsAutobus[0]?.idProduitAge;

    const campJourItem = {
      id: campItemId,
      name,
      type: CartItemType.CampJour,
      inscriptionCamp,
      trajetsAutobus: allTrajetsAutobus,
      idsReservationTemp: allIdsReservationTemp,
      formulaire: {},
      inclus,
      montant,
      taxableTps: false,
      taxableTvq: false,
      tps: 0,
      tvq: 0,
      total: montant,
      ...(firstIdProduitAge ? { idProduitAge: firstIdProduitAge } : {})
    };

    const currentCart = store.getState().shop.cart || [];
    const existingIndex = existingItem ? currentCart.findIndex(item => item === existingItem) : -1;
    if (existingIndex >= 0) {
      await dispatch(updateShop(existingIndex, campJourItem, false, language));
    } else {
      await dispatch(addToShoppingCart(campJourItem, false, language));
    }

    const cartAfterUpdate = store.getState().shop.cart || [];
    const legacyToRemove = getCampJourCartItemsForChild(cartAfterUpdate, childNum).filter(
      item => item.id && item.id.startsWith(`${campItemId}_`)
    );
    for (const item of legacyToRemove) {
      await dispatch(removeFromShoppingCart(item, false, language));
    }
  };

  const refreshSemainesAfterTrajetChange = useCallback((idsToDelete, childIndex) => {
    const panier = store.getState().shop;
    setIsLoadingSemaines(true);
    panier.idsReservationTemp = idsReservationTempMapRef.current[childIndex];

    const doFetch = () =>
      getSemainesCampJour(panier)
        .then(response => {
          const data = response?.data || response || [];
          setSemainesData(data);
        })
        .catch(err => console.error('Error fetching semaines data:', err))
        .finally(() => setIsLoadingSemaines(false));

    const promise = idsToDelete.length > 0
      ? deleteReservationTemp(idsToDelete).then(() => doFetch())
      : doFetch();

    return promise.finally(() => setIsTrajetLoading(false));
  }, [store]);

  const handleTrajetChange = (childIndex, weekId, trajetValue) => {
    if (trajetValue) {
      setIsTrajetLoading(true);
      const fieldId = `enfant${childIndex + 1}_semaines`;
      setErrors(prev => clearFieldError(prev, fieldId));
    }

    const idsToDelete = idsReservationTempMapRef.current[childIndex]?.[weekId]
      ? [...idsReservationTempMapRef.current[childIndex][weekId]]
      : [];
    if (idsReservationTempMapRef.current[childIndex]) {
      delete idsReservationTempMapRef.current[childIndex][weekId];
    }

    setEnfantsSelections(prev => {
      const newSelections = { ...prev };
      if (!newSelections[childIndex]) {
        newSelections[childIndex] = {};
      }
      if (!newSelections[childIndex][weekId]) {
        newSelections[childIndex][weekId] = { selected: true, trajet: null };
      }
      newSelections[childIndex][weekId].trajet = trajetValue || null;

      const fieldId = `enfant${childIndex + 1}_semaines`;
      onChange(fieldId, newSelections[childIndex]);

      if (newSelections[childIndex][weekId].selected && trajetValue) {
        addWeekToReservationTemp(childIndex, weekId, trajetValue).then(() =>
          updateCampJourCartItem(childIndex, newSelections[childIndex])
        ).then(() => refreshSemainesAfterTrajetChange(idsToDelete, childIndex))
          .catch(() => { });
      } else if (trajetValue) {
        updateCampJourCartItem(childIndex, newSelections[childIndex])
          .then(() => refreshSemainesAfterTrajetChange(idsToDelete, childIndex))
          .catch(() => { });
      } else {
        updateCampJourCartItem(childIndex, newSelections[childIndex]);
      }

      return newSelections;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const validateForm = () => {
    const { errors: newErrors, isValid } = validateCampDeJourStep({
      hasCampJourInCart,
      nombreEnfants,
      enfantsSelections,
      language,
      t
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (!validateForm()) {
      return;
    }
    goToNextStep();
  };

  const handleFillAllRequiredFields = () => {
    const num = parseInt(nombreEnfants, 10);
    if (!nombreEnfants || nombreEnfants === '') {
      setNombreEnfants('2');
      onChange('nombreEnfants', '2');
    } else if (num < 1) {
      setNombreEnfants('1');
      onChange('nombreEnfants', '1');
    } else if (num > MAX_ENFANTS) {
      setNombreEnfants(String(MAX_ENFANTS));
      onChange('nombreEnfants', String(MAX_ENFANTS));
    }
  };

  return (
    <div className="camp-de-jour-step">
      <h2>{t.titleStep1CampDeJour}</h2>

      {errors.panier && (
        <div className="camp-de-jour-step-panier-error">
          <span className="camp-color-error">{errors.panier}</span>
        </div>
      )}

      <div className="camp-de-jour-step-content">
        <div className="camp-field-group">
          <div className="formulaire-dynamique-label-wrapper">
            <span className="formulaire-dynamique-span">{t.nombreEnfants} * :</span>
          </div>
          <div className="input-container">
            <input
              type="text"
              inputMode="numeric"
              disabled={isTrajetLoading}
              className={`profile-input camp-de-jour-step-age-input ${errors.nombreEnfants ? 'camp-input-error' : ''}`}
              value={nombreEnfants}
              onChange={handleNombreEnfantsChange}
              placeholder={t.nombreEnfantsPlaceholder}
              maxLength={String(MAX_ENFANTS).length}
            />
            {errors.nombreEnfants && (
              <span className="camp-color-error formulaire-dynamique-span">{errors.nombreEnfants}</span>
            )}
          </div>
        </div>

        {parseInt(nombreEnfants) > 0 && (
          <div className="camp-enfants-sections">
            {(!Array.isArray(semainesData) || semainesData.length === 0)
              ? (
                <div className="camp-field-group">
                  <span className="formulaire-dynamique-span">
                    {language === 'fr' ? 'Chargement...' : 'Loading...'}
                    <LoadingSpinner height="90px" margin="0" display="block" background="transparent" />
                  </span>
                </div>
              ) : (
                Array.from({ length: parseInt(nombreEnfants) || 0 }, (_, childIndex) => {
                  const weeks = getUniqueWeeks();
                  const childSelections = enfantsSelections[childIndex] || {};

                  return (
                    <div key={childIndex} className="camp-enfant-section">
                      <h3 className="camp-enfant-title">{language === 'fr' ? `Enfant ${childIndex + 1}` : `Child ${childIndex + 1}`}</h3>
                      <div className="camp-semaines-table">
                        <div className="camp-semaines-header">
                          <div className="camp-semaine-col-checkbox"></div>
                          <div className="camp-semaine-col-label">{language === 'fr' ? 'Sélection des semaines' : 'Week selection'}</div>
                          <div className="camp-semaine-col-dates">{language === 'fr' ? 'Dates' : 'Dates'}</div>
                          <div className="camp-semaine-col-trajets">{language === 'fr' ? "Arrêt d'autobus" : 'Bus stop'}</div>
                        </div>
                        {weeks.map((week, weekIndex) => {
                          const weekSelection = childSelections[week.id] || { selected: false, trajet: null };
                          const isSelected = weekSelection.selected;
                          const placesLeftForWeek = week.placesRestantesEffectiveSemaine ?? week.placesRestantesSemaine ?? 0;
                          const isComplet = placesLeftForWeek <= 0;
                          const isBlocked = week.isBlocked || false;

                          return (
                            <div key={weekIndex} className="camp-semaine-row">
                              <div className="camp-semaine-col-checkbox">
                                {(!isBlocked || isSelected) && (!isComplet || isSelected) && (
                                  <input
                                    type="checkbox"
                                    id={`enfant${childIndex + 1}_semaine${weekIndex}`}
                                    checked={isSelected}
                                    onChange={() => handleWeekToggle(childIndex, week.id)}
                                    disabled={
                                      (isComplet && !isSelected)
                                      || isTrajetLoading
                                      || isLoadingSemaines
                                    }
                                  />
                                )}
                              </div>
                              <div className="camp-semaine-col-label">
                                <label htmlFor={`enfant${childIndex + 1}_semaine${weekIndex}`}>
                                  {formatWeekLabel(week.label, language)}
                                </label>
                              </div>
                              <div className="camp-semaine-col-dates">
                                {week.dateDebut && week.dateFin ? (
                                  <span>
                                    {formatDate(week.dateDebut)} {language === 'fr' ? 'au' : 'to'} {formatDate(week.dateFin)}
                                  </span>
                                ) : (
                                  <span>-</span>
                                )}
                              </div>

                              {(isTrajetLoading || isLoadingSemaines) && (
                                <span className="camp-trajet-loading">
                                  <LoadingSpinner height="90px" margin="0" display="block" background="transparent" />
                                </span>
                              )}

                              <div className="camp-semaine-col-trajets">
                                {(isBlocked || isComplet) && !isSelected ? (
                                  <span className="form-camp-complet">
                                    {language === 'fr' ? 'COMPLET' : 'FULL'}
                                  </span>
                                ) : isSelected ? (
                                  <div className="camp-trajet-select-container">

                                    <select
                                      className="profile-input camp-trajet-select"
                                      value={weekSelection.trajet || ''}
                                      onChange={(e) => handleTrajetChange(childIndex, week.id, e.target.value)}
                                      disabled={isTrajetLoading || isLoadingSemaines}
                                    >
                                      {!weekSelection.trajet && (
                                        <option value="">{language === 'fr' ? 'Choisir' : 'Choose'}</option>
                                      )}
                                      {week.trajets.map((trajet, trajetIndex) => (
                                        <option
                                          key={trajetIndex}
                                          value={trajet.value}
                                          disabled={trajet.isDisabled}
                                        >
                                          {trajet.label}
                                          {trajet.isDisabled && trajet.value !== weekSelection.trajet
                                            ? ` (${language === 'fr' ? 'COMPLET' : 'FULL'})` : ''}
                                        </option>
                                      ))}
                                    </select>
                                    {weekSelection.trajet && (() => {
                                      const selectedTrajet = week.trajets.find(t => t.value === weekSelection.trajet);
                                      const numEnfants = parseInt(nombreEnfants) || 0;
                                      if (selectedTrajet
                                        && selectedTrajet.minPlaces < numEnfants
                                        && selectedTrajet.label !== t.jAmeneMonEnfant
                                      ) {
                                        const places = selectedTrajet.minPlaces;
                                        const displayText = places === 1
                                          ? (language === 'fr' ? 'Dernière place' : 'Last spot')
                                          : language === 'fr'
                                            ? (places <= 0 ? '' : `${places} place${places > 1 ? 's' : ''} disponible${places > 1 ? 's' : ''}`)
                                            : (places <= 0 ? '' : `${places} spot${places > 1 ? 's' : ''} available`);
                                        if (!displayText) return null;
                                        return (
                                          <span className="camp-places-disponibles" style={{ marginLeft: '10px', fontSize: '14px' }}>
                                            {displayText}
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                ) : (
                                  <span className="camp-trajet-placeholder">-</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {errors[`enfant${childIndex + 1}_semaines`] && (
                        <span className="color-error formulaire-dynamique-span" style={{ display: 'block', marginTop: '10px' }}>
                          {errors[`enfant${childIndex + 1}_semaines`]}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
          </div>
        )}
      </div>

      <div className="camp-de-jour-step-actions">
        {currentStep > 0 && (
          <button
            type="button"
            className="camp-previous-button"
            onClick={goToPreviousStep}
            disabled={isTrajetLoading}
          >
            {t.precedent}
          </button>
        )}
        <button
          type="button"
          className="camp-next-button"
          onClick={handleNext}
          disabled={isTrajetLoading}
        >
          {t.suivant}
        </button>
      </div>
    </div>
  );
};

// Static validation
CampDeJourStep.validate = (formData, language = 'fr') => {
  const errors = {};

  const nombreEnfants = formData.nombreEnfants;
  if (!nombreEnfants || nombreEnfants.toString().trim() === '') {
    errors.nombreEnfants = language === 'fr'
      ? 'Le nombre d\'enfants est requis'
      : 'Number of children is required';
  } else {
    const nombreNum = parseInt(nombreEnfants);
    if (isNaN(nombreNum) || nombreNum < 1) {
      errors.nombreEnfants = language === 'fr'
        ? 'Le nombre d\'enfants doit être au moins 1'
        : 'Number of children must be at least 1';
    } else if (nombreNum > MAX_ENFANTS) {
      errors.nombreEnfants = language === 'fr'
        ? `Le nombre d'enfants ne peut pas dépasser ${MAX_ENFANTS}`
        : `Number of children cannot exceed ${MAX_ENFANTS}`;
    } else {
      // Validate that each child has at least one week selected with a trajet
      for (let i = 0; i < nombreNum; i++) {
        const fieldId = `enfant${i + 1}_semaines`;
        const childSelections = formData[fieldId] || {};
        const hasValidSelection = Object.values(childSelections).some(
          week => week && week.selected && week.trajet
        );
        if (!hasValidSelection) {
          errors[fieldId] = language === 'fr'
            ? `Veuillez sélectionner au moins une semaine avec un trajet pour l'enfant ${i + 1}`
            : `Please select at least one week with a route for child ${i + 1}`;
        }
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

CampDeJourStep.propTypes = {
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  goToNextStep: PropTypes.func.isRequired,
  goToPreviousStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func,
  formData: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  setFormData: PropTypes.func,
  validate: PropTypes.func,
  match: PropTypes.shape({
    params: PropTypes.object,
    path: PropTypes.string,
    url: PropTypes.string
  }),
  language: PropTypes.string,
  codeClient: PropTypes.string
};

CampDeJourStep.defaultProps = {
  goToStep: undefined,
  setFormData: undefined,
  validate: undefined,
  match: undefined,
  language: 'fr',
  codeClient: ''
};

export default CampDeJourStep;

